import { useUserInfo } from "@/api/user";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import Sentry from "@/integrations/Sentry";
import { cn } from "@/utils";
import { toPng } from "html-to-image";
import {
  Crop,
  ImagePlus,
  Loader2,
  MessageSquarePlus,
  Video,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";

type FeedbackTag = "bug" | "feature_request" | "enhancement" | "other";

const TAG_OPTIONS: {
  value: FeedbackTag;
  label: string;
  placeholder: string;
}[] = [
  {
    value: "bug",
    label: "Bug",
    placeholder:
      "What went wrong? Steps to reproduce, what you expected, what actually happened…",
  },
  {
    value: "feature_request",
    label: "Feature Request",
    placeholder:
      "What would you like us to build? Who's it for, and what problem does it solve?",
  },
  {
    value: "enhancement",
    label: "Enhancement",
    placeholder:
      "What existing feature could work better? What's the current friction, and how would you improve it?",
  },
  {
    value: "other",
    label: "Other",
    placeholder: "Anything else on your mind?",
  },
];

const MAX_SCREENSHOTS = 5;
/**
 * Marker attribute used to (a) tag any DOM that should be omitted from the
 * region snip via `html-to-image`'s `filter`, and (b) to look up nodes that
 * should also be visually hidden for the brief instant of capture (e.g. the
 * popover, which we keep mounted so the user doesn't lose their draft).
 */
const SNIP_OMIT_ATTR = "data-feedback-snip-omit";

/**
 * 1x1 transparent PNG. Used as `imagePlaceholder` for `html-to-image` so a
 * single failed cross-origin image (e.g. a gravatar avatar) doesn't reject
 * the whole capture.
 */
const TRANSPARENT_PIXEL_DATA_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=";

async function fileToUint8Array(file: File): Promise<Uint8Array> {
  const buf = await file.arrayBuffer();
  return new Uint8Array(buf);
}

interface SnipRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Fullscreen overlay that lets the user drag a rectangle and returns a
 * cropped PNG of the page region beneath the selection. Captures the page
 * via `html-to-image` (no browser permission prompt, no native picker).
 */
function RegionSnipper({
  onComplete,
  onCancel,
}: {
  onComplete: (file: File) => void;
  onCancel: () => void;
}) {
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [current, setCurrent] = useState<{ x: number; y: number } | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Defer reading `document.body` until after mount. Avoids "Target container
  // is not a DOM element." errors in any environment where the DOM isn't
  // synchronously available during the first render (SSR hydration, tests,
  // edge cases during React re-mounts, etc.).
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalTarget(typeof document !== "undefined" ? document.body : null);
  }, []);

  const rect: SnipRect | null = (() => {
    if (!start || !current) return null;
    const x = Math.min(start.x, current.x);
    const y = Math.min(start.y, current.y);
    const w = Math.abs(start.x - current.x);
    const h = Math.abs(start.y - current.y);
    return { x, y, w, h };
  })();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onCancel]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setStart({ x: e.clientX, y: e.clientY });
    setCurrent({ x: e.clientX, y: e.clientY });
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!start) return;
      setCurrent({ x: e.clientX, y: e.clientY });
    },
    [start],
  );

  const handleMouseUp = useCallback(async () => {
    if (!rect || rect.w < 4 || rect.h < 4) {
      setStart(null);
      setCurrent(null);
      return;
    }

    setIsCapturing(true);

    // Briefly hide everything tagged for omission (this overlay + the still-
    // mounted popover) so they don't appear in the capture. We toggle
    // visibility rather than display so layout doesn't reflow underneath us.
    const omittedNodes = Array.from(
      document.querySelectorAll<HTMLElement>(`[${SNIP_OMIT_ATTR}]`),
    );
    const previousVisibilities = omittedNodes.map((n) => n.style.visibility);
    omittedNodes.forEach((n) => {
      n.style.visibility = "hidden";
    });
    // Yield two animation frames so the visibility change actually paints
    // before html-to-image clones the DOM.
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    await new Promise<void>((r) => requestAnimationFrame(() => r()));

    try {
      const dpr = window.devicePixelRatio || 1;
      const dataUrl = await toPng(document.documentElement, {
        cacheBust: true,
        pixelRatio: dpr,
        // Skip embedding @font-face from cross-origin sheets (e.g. Google
        // Fonts) — accessing their cssRules throws SecurityError and aborts
        // the whole capture. We don't need exact font fidelity for a snip.
        skipFonts: true,
        // Substitute a transparent pixel for any image that fails to load
        // (CORS-blocked gravatars, expired URLs, etc.) so a single broken
        // <img> doesn't poison the entire snapshot.
        imagePlaceholder: TRANSPARENT_PIXEL_DATA_URL,
        // `imagePlaceholder` only covers the *fetch* step inside
        // html-to-image. If a cloned <img> still fails to decode (e.g. its
        // src is an empty string after a failed fetch, or the placeholder
        // itself is rejected by some upstream policy), html-to-image rejects
        // the whole capture with the raw DOM `error` Event. Swallowing the
        // error here lets the rest of the page render — the broken image
        // simply appears blank in the snapshot. Without this, a single
        // failing thumbnail (e.g. an ImageCardCanvas/VideoCardCanvas in a
        // workspace canvas) takes down the entire region snip.
        onImageErrorHandler: () => undefined,
        // Belt-and-suspenders: skip any node tagged for omission, and any
        // descendant of one (in case the marker is on an ancestor).
        filter: (node) => {
          if (!(node instanceof HTMLElement)) return true;
          return !node.closest(`[${SNIP_OMIT_ATTR}]`);
        },
      });

      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Failed to decode page snapshot"));
        img.src = dataUrl;
      });

      const cropX = (rect.x + window.scrollX) * dpr;
      const cropY = (rect.y + window.scrollY) * dpr;
      const cropW = rect.w * dpr;
      const cropH = rect.h * dpr;

      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(cropW));
      canvas.height = Math.max(1, Math.round(cropH));
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas 2D context unavailable");
      ctx.drawImage(
        img,
        cropX,
        cropY,
        cropW,
        cropH,
        0,
        0,
        canvas.width,
        canvas.height,
      );

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      if (!blob) throw new Error("Failed to encode region");
      const file = new File([blob], `snip_${Date.now()}.png`, {
        type: "image/png",
      });
      onComplete(file);
    } catch (err) {
      console.error("Region snip failed", err);
      toast.error("Couldn't capture that region. Try again.");
      onCancel();
    } finally {
      // Always restore visibility, even on error, so the user isn't left
      // staring at a blank screen.
      omittedNodes.forEach((n, i) => {
        n.style.visibility = previousVisibilities[i] ?? "";
      });
    }
  }, [rect, onComplete, onCancel]);

  // Portal to <body> so the overlay escapes any transformed ancestor (e.g.
  // the Sidebar, which uses `translate-x-*` and therefore creates a new
  // containing block that would otherwise constrain our `fixed` positioning).
  if (!portalTarget) return null;

  return createPortal(
    <div
      ref={overlayRef}
      {...{ [SNIP_OMIT_ATTR]: "true" }}
      className="fixed inset-0 z-[100000] cursor-crosshair select-none"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Dim backdrop with rectangular cutout */}
      {rect ? (
        <>
          {/* top */}
          <div
            className="absolute left-0 right-0 bg-n-95/40 dark:bg-n-00/40"
            style={{ top: 0, height: rect.y }}
          />
          {/* bottom */}
          <div
            className="absolute left-0 right-0 bg-n-95/40 dark:bg-n-00/40"
            style={{ top: rect.y + rect.h, bottom: 0 }}
          />
          {/* left */}
          <div
            className="absolute bg-n-95/40 dark:bg-n-00/40"
            style={{ top: rect.y, height: rect.h, left: 0, width: rect.x }}
          />
          {/* right */}
          <div
            className="absolute bg-n-95/40 dark:bg-n-00/40"
            style={{
              top: rect.y,
              height: rect.h,
              left: rect.x + rect.w,
              right: 0,
            }}
          />
          {/* selection border */}
          <div
            className="absolute border-2 border-primary shadow-[0_0_0_1px_rgba(255,255,255,0.5)] pointer-events-none"
            style={{
              left: rect.x,
              top: rect.y,
              width: rect.w,
              height: rect.h,
            }}
          />
          {/* dimensions label */}
          <div
            className="absolute text-[11px] font-mono px-1.5 py-0.5 rounded bg-primary text-primary-foreground pointer-events-none"
            style={{
              left: Math.min(rect.x, window.innerWidth - 80),
              top:
                rect.y + rect.h + 6 > window.innerHeight - 24
                  ? rect.y - 22
                  : rect.y + rect.h + 6,
            }}
          >
            {Math.round(rect.w)} × {Math.round(rect.h)}
          </div>
        </>
      ) : (
        <div className="absolute inset-0 bg-n-95/30 dark:bg-n-00/30" />
      )}

      {/* Instructions / status pill */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 pointer-events-none">
        <div className="flex items-center gap-2 rounded-full bg-background/90 backdrop-blur border px-3 py-1.5 text-xs text-foreground shadow-lg">
          {isCapturing ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Capturing region…</span>
            </>
          ) : (
            <>
              <Crop className="h-3.5 w-3.5 text-primary" />
              <span>Drag to select a region</span>
              <span className="text-muted-foreground">·</span>
              <kbd className="font-mono text-[10px] px-1 rounded border bg-muted/50">
                Esc
              </kbd>
              <span className="text-muted-foreground">to cancel</span>
            </>
          )}
        </div>
      </div>
    </div>,
    portalTarget,
  );
}

interface InternalFeedbackWidgetProps {
  /**
   * Optional render function for the trigger element. When provided, the
   * widget renders this in place of the default fixed-position floating
   * button. The render function receives `open`, `toggle`, and the current
   * `isOpen` state so the trigger can reflect open state if desired.
   */
  renderTrigger?: (props: {
    open: () => void;
    toggle: () => void;
    isOpen: boolean;
  }) => React.ReactNode;
  /** Side of the trigger to anchor the popover (default: bottom). */
  side?: "top" | "right" | "bottom" | "left";
  /** Alignment along the side (default: end). */
  align?: "start" | "center" | "end";
  /** Pixel offset from the trigger along the side axis (default: 8). */
  sideOffset?: number;
  /**
   * Offset along the alignment axis. With `align="end"`, a negative value
   * pushes the popover further toward the trigger's end edge (e.g. closer to
   * the viewport's right edge).
   */
  alignOffset?: number;
}

export function InternalFeedbackWidget({
  renderTrigger,
  side = "bottom",
  align = "end",
  sideOffset = 8,
  alignOffset = 0,
}: InternalFeedbackWidgetProps = {}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSnipping, setIsSnipping] = useState(false);
  const [tag, setTag] = useState<FeedbackTag>("bug");
  const [comment, setComment] = useState("");
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: userInfo } = useUserInfo();
  const userEmail = userInfo?.user?.email ?? "";
  const userName = userInfo?.user?.name ?? "";

  /**
   * Reset the form. Called only after a successful submit — closing the
   * popover preserves the draft so users can step away to grab a screenshot
   * or check something without losing what they typed.
   */
  const reset = useCallback(() => {
    setTag("bug");
    setComment("");
    setScreenshots([]);
    setIsSubmitting(false);
  }, []);

  const openDialog = useCallback(() => setIsOpen(true), []);
  const toggleDialog = useCallback(() => setIsOpen((prev) => !prev), []);

  const addScreenshot = useCallback((file: File) => {
    setScreenshots((prev) => {
      if (prev.length >= MAX_SCREENSHOTS) return prev;
      return [...prev, file];
    });
  }, []);

  const removeScreenshot = useCallback((index: number) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSnipRegion = useCallback(() => {
    if (screenshots.length >= MAX_SCREENSHOTS) return;
    // Keep the popover open during snipping so the user doesn't lose their
    // place in the form. The snipper overlay sits above it (z-100000) and
    // captures all input until the user finishes or cancels. The popover is
    // visually faded + made non-interactive while `isSnipping` is true.
    setIsSnipping(true);
  }, [screenshots.length]);

  const handleSnipComplete = useCallback(
    (file: File) => {
      addScreenshot(file);
      setIsSnipping(false);
    },
    [addScreenshot],
  );

  const handleSnipCancel = useCallback(() => {
    setIsSnipping(false);
  }, []);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            addScreenshot(file);
            e.preventDefault();
            break;
          }
        }
      }
    },
    [addScreenshot],
  );

  const handleUploadClick = useCallback(() => {
    if (screenshots.length >= MAX_SCREENSHOTS) return;
    fileInputRef.current?.click();
  }, [screenshots.length]);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []).filter((f) =>
        f.type.startsWith("image/"),
      );
      for (const file of files) {
        addScreenshot(file);
      }
      // Reset so picking the same file twice in a row still fires `change`.
      e.target.value = "";
    },
    [addScreenshot],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const files = Array.from(e.dataTransfer.files).filter((f) =>
        f.type.startsWith("image/"),
      );
      for (const file of files) {
        addScreenshot(file);
      }
    },
    [addScreenshot],
  );

  const handleSubmit = useCallback(async () => {
    if (!comment.trim()) return;
    setIsSubmitting(true);
    try {
      const attachments = await Promise.all(
        screenshots.map(async (file) => ({
          filename: file.name,
          data: await fileToUint8Array(file),
          contentType: file.type,
        })),
      );

      const tagLabel = TAG_OPTIONS.find((o) => o.value === tag)?.label ?? tag;

      Sentry.captureFeedback(
        {
          name: userName,
          email: userEmail,
          message: `[${tagLabel}] ${comment.trim()}`,
          url: window.location.href,
        },
        {
          includeReplay: true,
          attachments,
        },
      );

      toast.success("Feedback submitted -- thank you!");
      reset();
      setIsOpen(false);
    } catch {
      toast.error("Failed to submit feedback. Please try again.");
      setIsSubmitting(false);
    }
  }, [comment, screenshots, tag, userName, userEmail, reset]);

  const canSubmit = comment.trim().length > 0 && !isSubmitting;

  return (
    <>
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        // Non-modal so the user can still see/scroll/click the page behind the
        // popover while drafting feedback.
        modal={false}
      >
        <PopoverAnchor>
          {renderTrigger ? (
            renderTrigger({
              open: openDialog,
              toggle: toggleDialog,
              isOpen,
            })
          ) : (
            <button
              type="button"
              onClick={toggleDialog}
              className={cn(
                "fixed bottom-4 right-4 z-50",
                "h-10 w-10 rounded-full",
                "bg-primary text-primary-foreground",
                "shadow-lg hover:shadow-xl",
                "flex items-center justify-center",
                "transition-all hover:scale-105 active:scale-95",
                "cursor-pointer",
              )}
              title="Send feedback"
              aria-label="Send feedback"
            >
              <MessageSquarePlus className="h-5 w-5" />
            </button>
          )}
        </PopoverAnchor>
        <PopoverContent
          side={side}
          align={align}
          sideOffset={sideOffset}
          alignOffset={alignOffset}
          collisionPadding={20}
          className={cn(
            "w-[420px] p-2 rounded-2xl border dark:border-white/[0.08]",
            "shadow-xl shadow-black/10 bg-background",
            "transition-opacity duration-150",
            // While snipping, fade the popover and disable its pointer
            // events so the snipper overlay catches the drag — but keep it
            // mounted so the user's draft is preserved and faintly visible.
            isSnipping && "opacity-30 pointer-events-none",
          )}
          onPaste={handlePaste}
          // Don't auto-close on outside click — preserves the draft and lets
          // the user click around the page to find what they want to capture.
          onInteractOutside={(e) => e.preventDefault()}
          {...{ [SNIP_OMIT_ATTR]: "true" }}
        >
          {/* Header — extends edge-to-edge by cancelling the popover's p-2 */}
          <div
            className="flex items-center gap-2 px-4 py-3 -mx-2 -mt-2 mb-1 rounded-t-2xl
                       bg-gradient-to-br from-violet-500/[0.12] via-violet-500/[0.07] to-plum/[0.04]
                       dark:from-violet-400/[0.16] dark:via-violet-400/[0.09] dark:to-plum/[0.05]
                       border-b border-violet-500/[0.10] dark:border-violet-400/[0.12]"
          >
            <MessageSquarePlus className="h-[15px] w-[15px] text-violet-600 dark:text-spectrum-violet shrink-0" />
            <span className="flex-1 text-[13px] font-semibold text-foreground">
              Send feedback
            </span>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close"
              className="flex h-6 w-6 items-center justify-center rounded-md
                         text-muted-foreground/70 hover:text-foreground
                         hover:bg-foreground/[0.05] dark:hover:bg-n-95/[0.05]
                         transition-colors cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Category */}
          <div className="px-3 py-2">
            <Select value={tag} onValueChange={(v) => setTag(v as FeedbackTag)}>
              <SelectTrigger
                id="feedback-tag"
                className="h-8 text-[13px] rounded-lg dark:border-white/[0.08] bg-foreground/[0.02] dark:bg-n-95/[0.02] hover:bg-foreground/[0.04] dark:hover:bg-n-95/[0.04] transition-colors"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TAG_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="my-1 h-px bg-border/40 dark:bg-n-95/[0.06]" />

          {/* Comment */}
          <div className="px-3 py-2">
            <Textarea
              id="feedback-comment"
              placeholder={
                TAG_OPTIONS.find((o) => o.value === tag)?.placeholder ??
                "Describe the issue or share an idea…"
              }
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="min-h-[160px] resize-none text-[13px] rounded-lg dark:border-white/[0.08] bg-foreground/[0.02] dark:bg-n-95/[0.02] focus-visible:ring-1 focus-visible:ring-ring/50"
            />
            <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Video className="h-3 w-3 shrink-0" />A session replay will be
              attached to this feedback.
            </p>
          </div>

          <div className="my-1 h-px bg-border/40 dark:bg-n-95/[0.06]" />

          {/* Screenshots */}
          <div className="px-3 py-2 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-medium text-foreground/75">
                Screenshots
              </span>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {screenshots.length}/{MAX_SCREENSHOTS}
              </span>
            </div>

            {screenshots.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {screenshots.map((file, i) => (
                  <ScreenshotThumb
                    key={`${file.name}-${i}`}
                    file={file}
                    index={i}
                    onRemove={removeScreenshot}
                  />
                ))}
              </div>
            )}

            {screenshots.length < MAX_SCREENSHOTS && (
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                className="space-y-1.5"
              >
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    type="button"
                    onClick={handleSnipRegion}
                    title="Drag a rectangle to capture part of the page"
                    className="flex items-center gap-2 h-9 rounded-lg px-3 text-left text-[13px] font-medium text-foreground/75 border dark:border-white/[0.08] bg-foreground/[0.02] dark:bg-n-95/[0.02] hover:bg-foreground/[0.05] dark:hover:bg-n-95/[0.05] dark:hover:border-white/[0.12] transition-colors cursor-pointer"
                  >
                    <Crop className="h-[15px] w-[15px] shrink-0 text-muted-foreground/70" />
                    <span className="flex-1 truncate">Snip a region</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    title="Upload an image from your computer"
                    className="flex items-center gap-2 h-9 rounded-lg px-3 text-left text-[13px] font-medium text-foreground/75 border dark:border-white/[0.08] bg-foreground/[0.02] dark:bg-n-95/[0.02] hover:bg-foreground/[0.05] dark:hover:bg-n-95/[0.05] dark:hover:border-white/[0.12] transition-colors cursor-pointer"
                  >
                    <ImagePlus className="h-[15px] w-[15px] shrink-0 text-muted-foreground/70" />
                    <span className="flex-1 truncate">Upload screenshot</span>
                  </button>
                </div>
                <p className="text-[11px] leading-snug text-muted-foreground">
                  Having trouble snipping? Upload a screenshot manually or paste
                  one from your clipboard.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileInputChange}
                  className="hidden"
                />
              </div>
            )}
          </div>

          <div className="my-1 h-px bg-border/40 dark:bg-n-95/[0.06]" />

          {/* Footer */}
          <div className="flex items-center gap-2 px-3 py-2">
            <span className="flex-1 min-w-0 truncate text-[11px] text-muted-foreground">
              {userEmail ? `Sending as ${userEmail}` : "Anonymous feedback"}
            </span>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => setIsOpen(false)}
              className="h-7 rounded-lg text-[12px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </Button>
            <Button
              size="xs"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="h-7 rounded-lg text-[12px]"
            >
              {isSubmitting && (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
              )}
              Submit
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {isSnipping && (
        <RegionSnipper
          onComplete={handleSnipComplete}
          onCancel={handleSnipCancel}
        />
      )}
    </>
  );
}

/**
 * Thumbnail with a stable object URL that's revoked on unmount, so removing
 * a screenshot doesn't leak (and re-adding the same File doesn't re-decode).
 */
function ScreenshotThumb({
  file,
  index,
  onRemove,
}: {
  file: File;
  index: number;
  onRemove: (index: number) => void;
}) {
  const [url, setUrl] = useState<string>("");

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <div className="relative group h-14 w-14 rounded-md overflow-hidden border bg-muted">
      {url && (
        <img
          src={url}
          alt={`Screenshot ${index + 1}`}
          className="h-full w-full object-cover"
        />
      )}
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="absolute top-0 right-0 h-4 w-4 bg-destructive text-destructive-foreground rounded-bl-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
        aria-label={`Remove screenshot ${index + 1}`}
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}
